// PhotoMarkupStage.jsx — the big photo in the register's photo sheet and the
// item photo viewer, with three mark-up tools: pen, arrow and text.
//
// v0.262.0. Brett, on the v0.261 sheet: "this should be a larger photo with the
// filmstrip at the bottom. I also have markup ability of the photo and save as
// a copy or save over the original."
// v0.279.0. Brett: "dont forget the ability to markup and post it to
// conversation Just 3 tools, text, arrow and pen." Pen stays the default; the
// arrow is a drag from tail to point; text is a tap where the words go, then
// type (Enter or another tap keeps them, Esc drops them). What the buttons do
// is the caller's (`actions`): the register saves a copy or over the original,
// the item viewer posts to the conversation.
//
// The photo is fitted in a dark box and zooms with the shared usePanZoom
// (wheel, drag, pinch). Mark up turns the tools on: one finger or the mouse
// marks, pan and pinch are off (a finger cannot both pan and draw), the wheel
// still zooms so a small crack can be ringed closely. Each mark is kept in the
// photo's own 0..1 coordinates (utils/photoMarks.js), read through the
// picture's on-screen box, so a zoom, a resize or burning the marks into the
// full-size file puts every mark where it was made.
//
// Saving is the caller's job (onSave / onRestore): this component only hands
// over the marks, and burnMarks (below) draws them into the file as a JPEG.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pencil, ArrowUpRight, Type, Undo2, RotateCcw } from 'lucide-react';
import { theme, btnPrimary, btnSecondary } from '../theme';
import { usePanZoom } from '../utils/usePanZoom';
import {
  PEN_WIDTH, TEXT_SIZE, TEXT_MAX, MARK_FONT,
  kindOf, cleanText, arrowLongEnough, usableMarks, penPath, arrowGeometry, textBox, drawMarks,
} from '../utils/photoMarks';

// Line width as a share of the photo's shorter side — the same on screen and
// in the saved file, so what was drawn is what is kept.
export { PEN_WIDTH };

const cssColour = (name, fallback) => {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (v) return v;
  } catch (_e) { /* no document */ }
  return fallback;
};

// The marks burnt into the photo at its full size. `src` is the stored
// original (a signed storage link, read the way the PDF builder reads it);
// from-image applies the camera's rotation, so the marks land as drawn.
export async function burnMarks(src, marks) {
  const res = await fetch(src, { mode: 'cors', credentials: 'omit', cache: 'no-store' });
  if (!res.ok) throw new Error(`the photo could not be read (${res.status})`);
  const bmp = await createImageBitmap(await res.blob(), { imageOrientation: 'from-image' });
  try {
    const w = bmp.width, h = bmp.height;
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const g = cv.getContext('2d');
    g.drawImage(bmp, 0, 0, w, h);
    drawMarks(g, marks, w, h, {
      ink: cssColour('--danger', 'rgb(220, 38, 38)'),
      halo: cssColour('--on-chrome', 'rgb(255, 255, 255)'),
    });
    const blob = await new Promise((ok, no) => cv.toBlob(b => (b ? ok(b) : no(new Error('the marked-up photo could not be made'))), 'image/jpeg', 0.88));
    return { blob, width: w, height: h };
  } finally {
    if (bmp.close) bmp.close();
  }
}

// Words on the screen are placed by the same measure as in the file.
let measureCtx = null;
const measurer = () => {
  if (!measureCtx && typeof document !== 'undefined') {
    try { measureCtx = document.createElement('canvas').getContext('2d'); } catch (_e) { /* no canvas */ }
  }
  if (measureCtx) return measureCtx;
  const est = { font: '', measureText: (t) => ({ width: t.length * 0.6 * (parseFloat(/(\d+(?:\.\d+)?)px/.exec(est.font)?.[1]) || 12) }) };
  return est;
};

const TOOL_BUTTONS = [
  { tool: 'pen', label: 'Pen', Icon: Pencil },
  { tool: 'arrow', label: 'Arrow', Icon: ArrowUpRight },
  { tool: 'text', label: 'Text', Icon: Type },
];
const HINT = { pen: 'Draw on the photo', arrow: 'Drag to draw an arrow', text: 'Tap where the words go' };
const DEFAULT_ACTIONS = [
  { mode: 'copy', label: 'Save as copy' },
  { mode: 'over', label: 'Save over original', primary: true },
];

export default function PhotoMarkupStage({
  photo, index, count, onPrev, onNext,
  canDraw, cannotDrawWhy, busy, onSave, onRestore, onEditingChange,
  actions = DEFAULT_ACTIONS, startMarking = false, onCancel,
}) {
  const boxRef = useRef(null);
  const picRef = useRef(null);
  const inputRef = useRef(null);
  const [boxSize, setBoxSize] = useState({ w: 0, h: 0 });
  const [natural, setNatural] = useState(null); // { w, h } once the picture has loaded
  const [marking, setMarking] = useState(!!startMarking);
  const [tool, setTool] = useState('pen');
  const [marks, setMarks] = useState([]);
  const [typing, setTypingState] = useState(null); // { id, at, text } — words being typed
  const typingRef = useRef(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const drawingRef = useRef(null); // the stroke or arrow under the finger

  const setTyping = (v) => { typingRef.current = v; setTypingState(v); };

  const { isZoomed, dragging, reset: resetZoom, toggle: toggleZoom, transform, handlers, surfaceStyle } =
    usePanZoom(boxRef, { wheel: true, pinch: !marking });

  useEffect(() => { onEditingChange?.(marking); }, [marking, onEditingChange]);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const measure = () => setBoxSize({ w: box.clientWidth, h: box.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, []);

  // A new text box takes the keyboard. preventScroll: the box clips, it must
  // never be scrolled by a focus.
  useEffect(() => {
    if (typing && inputRef.current) {
      try { inputRef.current.focus({ preventScroll: true }); } catch (_e) { inputRef.current.focus(); }
    }
  }, [typing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The picture fitted inside the box, centred.
  let fit = null;
  if (natural && boxSize.w && boxSize.h) {
    const s = Math.min(boxSize.w / natural.w, boxSize.h / natural.h);
    const w = natural.w * s, h = natural.h * s;
    fit = { left: (boxSize.w - w) / 2, top: (boxSize.h - h) / 2, w, h };
  }

  const at = (e) => {
    const r = picRef.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
    return [Math.min(1, Math.max(0, x)), Math.min(1, Math.max(0, y))];
  };

  // The words being typed become a mark (blank words are dropped). Returns the
  // marks as they now are, for a save straight after.
  const commitTyping = () => {
    const t = typingRef.current;
    if (!t) return marks;
    setTyping(null);
    const text = cleanText(t.text);
    if (!text) return marks;
    const next = [...marks, { kind: 'text', at: t.at, text }];
    setMarks(next);
    return next;
  };

  const markDown = (e) => {
    if (!marking || busy || e.button > 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (tool === 'text') {
      commitTyping();
      setTyping({ id: `${Date.now()}-${Math.random()}`, at: at(e), text: '' });
      return;
    }
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* not a capture host */ }
    const p = at(e);
    const mark = tool === 'arrow' ? { kind: 'arrow', from: p, to: p } : { kind: 'pen', pts: [p] };
    drawingRef.current = { id: e.pointerId, mark };
    setMarks(m => [...m, mark]);
  };
  const markMove = (e) => {
    const d = drawingRef.current;
    if (!d || d.id !== e.pointerId) return;
    e.preventDefault();
    d.mark = kindOf(d.mark) === 'arrow' ? { ...d.mark, to: at(e) } : { ...d.mark, pts: [...d.mark.pts, at(e)] };
    setMarks(m => [...m.slice(0, -1), d.mark]);
  };
  const markUp = (e) => {
    const d = drawingRef.current;
    if (!d || d.id !== e.pointerId) return;
    drawingRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* already released */ }
    // A tap with the arrow is not an arrow.
    if (kindOf(d.mark) === 'arrow' && !arrowLongEnough(d.mark)) setMarks(m => m.slice(0, -1));
  };

  const pickTool = (t) => { commitTyping(); setTool(t); };
  const undo = () => {
    if (typingRef.current) { setTyping(null); return; }
    setMarks(m => m.slice(0, -1));
  };
  const stopMarking = () => {
    setMarking(false); setMarks([]); setTyping(null); setTool('pen'); drawingRef.current = null;
  };
  const cancel = () => { stopMarking(); onCancel?.(); };
  const save = async (mode) => {
    const final = usableMarks(commitTyping());
    if (!final.length) return;
    const ok = await onSave(mode, final);
    if (ok) stopMarking();
  };

  const url = photo ? (photo.url || photo.thumbnailUrl) : null;
  const marked = marks.length > 0 || (typing ? cleanText(typing.text) !== '' : false);
  const nw = natural ? natural.w : 1, nh = natural ? natural.h : 1;
  const lineWidth = Math.max(2, PEN_WIDTH * Math.min(nw, nh));
  const typingFont = fit ? Math.max(12, TEXT_SIZE * Math.min(nw, nh)) * (fit.w / nw) : 16;
  const arrow = (side) => ({
    position: 'absolute', top: '50%', [side]: 10, transform: 'translateY(-50%)', zIndex: 3,
    width: 40, height: 40, borderRadius: 999, border: 'none', cursor: 'pointer',
    background: 'rgba(0,0,0,0.45)', color: 'var(--on-chrome)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });
  const small = { ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px' };
  const picked = { background: 'var(--brand-50)', border: `1px solid ${theme.brand}`, color: theme.brand };

  return (
    <>
      <div
        ref={boxRef}
        data-photo-stage
        {...(marking ? {} : handlers)}
        onDoubleClick={marking ? undefined : () => toggleZoom()}
        style={{
          position: 'relative', flex: 1, minHeight: 220, overflow: 'hidden',
          background: 'var(--chrome-900)', ...(marking ? { touchAction: 'none' } : surfaceStyle),
          cursor: marking ? (tool === 'text' ? 'text' : 'crosshair') : (surfaceStyle.cursor || (url ? 'zoom-in' : 'default')),
        }}
      >
        {!url && (
          <div data-stage-empty style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', color: 'var(--on-chrome)', opacity: 0.75, fontSize: 14, lineHeight: 1.4,
          }}>
            No photos yet. Take a photo, choose photos, or drag images here.
          </div>
        )}
        {url && (
          <div style={{ transform, transformOrigin: '0 0', transition: dragging ? 'none' : 'transform 0.2s ease', width: '100%', height: '100%' }}>
            <div
              ref={picRef}
              data-photo-picture
              onPointerDown={markDown}
              onPointerMove={markMove}
              onPointerUp={markUp}
              onPointerCancel={markUp}
              // A tap that opens a text box must not then take the keyboard away from it.
              onMouseDown={marking && tool === 'text' ? (e) => e.preventDefault() : undefined}
              style={fit
                ? { position: 'absolute', left: fit.left, top: fit.top, width: fit.w, height: fit.h }
                : { position: 'absolute', inset: 0, visibility: 'hidden' }}
            >
              <img
                key={url}
                src={url}
                alt={photo.caption || `photo ${index + 1}`}
                draggable={false}
                onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth || 1, h: e.currentTarget.naturalHeight || 1 })}
                style={{ width: '100%', height: '100%', display: 'block', userSelect: 'none', pointerEvents: 'none' }}
              />
              {marks.length > 0 && (
                <svg
                  data-photo-marks={marks.length}
                  viewBox={`0 0 ${nw} ${nh}`}
                  preserveAspectRatio="none"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
                >
                  {marks.map((m, i) => {
                    const k = kindOf(m);
                    if (k === 'arrow') {
                      const g = arrowGeometry(m.from, m.to, nw, nh, lineWidth);
                      if (!g) return null;
                      return (
                        <g key={i} data-mark="arrow">
                          <path d={`M${g.shaft[0][0]} ${g.shaft[0][1]}L${g.shaft[1][0]} ${g.shaft[1][1]}`} fill="none"
                            stroke="var(--danger)" strokeWidth={lineWidth} strokeLinecap="round" />
                          <polygon points={g.head.map(([x, y]) => `${x},${y}`).join(' ')} fill="var(--danger)" />
                        </g>
                      );
                    }
                    if (k === 'text') {
                      const text = cleanText(m.text);
                      const b = textBox(measurer(), text, m.at, nw, nh);
                      return (
                        <text key={i} data-mark="text" x={b.x} y={b.y} dominantBaseline="central"
                          fontFamily={MARK_FONT} fontWeight={700} fontSize={b.size}
                          fill="var(--danger)" stroke="var(--on-chrome)" strokeWidth={b.halo} strokeLinejoin="round"
                          style={{ paintOrder: 'stroke' }}>{text}</text>
                      );
                    }
                    return (
                      <path key={i} data-mark="pen" d={penPath(m.pts, nw, nh)} fill="none" stroke="var(--danger)"
                        strokeWidth={lineWidth} strokeLinecap="round" strokeLinejoin="round" />
                    );
                  })}
                </svg>
              )}
              {typing && (
                <input
                  ref={inputRef}
                  data-mark-typing
                  aria-label="Words on the photo"
                  placeholder="Type here"
                  maxLength={TEXT_MAX}
                  value={typing.text}
                  onChange={(e) => setTyping({ ...typingRef.current, text: e.target.value })}
                  onPointerDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitTyping(); }
                    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setTyping(null); }
                  }}
                  style={{
                    position: 'absolute', left: `${typing.at[0] * 100}%`, top: `${typing.at[1] * 100}%`,
                    transform: 'translateY(-50%)', zIndex: 2, boxSizing: 'content-box',
                    width: `${Math.max(5, typing.text.length + 1)}ch`, maxWidth: '90%',
                    fontFamily: MARK_FONT, fontWeight: 700, fontSize: typingFont, lineHeight: 1.2,
                    color: 'var(--danger)', background: 'var(--surface-0)',
                    border: '1px dashed var(--danger)', borderRadius: 4, padding: '0 4px', outline: 'none',
                  }}
                />
              )}
            </div>
          </div>
        )}
        {!marking && index > 0 && (
          <button type="button" aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); onPrev(); }} style={arrow('left')}>
            <ChevronLeft size={22} aria-hidden />
          </button>
        )}
        {!marking && index < count - 1 && (
          <button type="button" aria-label="Next photo" onClick={(e) => { e.stopPropagation(); onNext(); }} style={arrow('right')}>
            <ChevronRight size={22} aria-hidden />
          </button>
        )}
        {isZoomed && (
          <button type="button" onClick={(e) => { e.stopPropagation(); resetZoom(); }} aria-label="Reset zoom" style={{
            position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 3,
            border: 'none', borderRadius: 999, padding: '5px 12px', cursor: 'pointer',
            background: 'rgba(0,0,0,0.55)', color: 'var(--on-chrome)', fontSize: 12, fontWeight: 700,
          }}>Reset zoom</button>
        )}
      </div>

      {/* The photo's own actions, under the picture. */}
      {url && (
        <div data-stage-actions style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
          padding: '8px 16px', borderBottom: `1px solid ${theme.border}`,
        }}>
          <span data-stage-counter style={{ fontSize: 12, color: theme.textLight, marginRight: 'auto' }}>
            {marking ? HINT[tool] : `${index + 1} of ${count}`}
          </span>
          {!marking && !confirmRestore && (
            <>
              {photo.hasOriginal && (
                <button type="button" disabled={busy} onClick={() => setConfirmRestore(true)} style={small} title="Put the photo back as it was taken">
                  <RotateCcw size={15} aria-hidden /> Restore original
                </button>
              )}
              {canDraw ? (
                <button type="button" disabled={busy} onClick={() => { resetZoom(); setMarking(true); }} style={small}>
                  <Pencil size={15} aria-hidden /> Mark up
                </button>
              ) : cannotDrawWhy ? (
                <span style={{ fontSize: 12, color: theme.textLight }}>{cannotDrawWhy}</span>
              ) : null}
            </>
          )}
          {!marking && confirmRestore && (
            <>
              <span style={{ fontSize: 12, color: theme.text }}>Restore the photo as taken? Its marks are lost.</span>
              <button type="button" disabled={busy} onClick={() => setConfirmRestore(false)} style={small}>Keep</button>
              <button type="button" disabled={busy}
                onClick={async () => { const ok = await onRestore(); if (ok) setConfirmRestore(false); }}
                style={{ ...btnPrimary, padding: '7px 12px' }}>Restore</button>
            </>
          )}
          {marking && (
            <>
              <span role="group" aria-label="Mark-up tool" data-mark-tools style={{ display: 'inline-flex', gap: 4 }}>
                {TOOL_BUTTONS.map(({ tool: t, label, Icon }) => (
                  <button key={t} type="button" data-tool={t} aria-pressed={tool === t} disabled={busy}
                    onClick={() => pickTool(t)} style={{ ...small, ...(tool === t ? picked : null) }}>
                    <Icon size={15} aria-hidden /> {label}
                  </button>
                ))}
              </span>
              <button type="button" disabled={busy || !marked} onClick={undo} style={small}>
                <Undo2 size={15} aria-hidden /> Undo
              </button>
              <button type="button" disabled={busy} onClick={cancel} style={small}>Cancel</button>
              {actions.map(a => (
                <button key={a.mode} type="button" disabled={busy || !marked} onClick={() => save(a.mode)}
                  style={a.primary ? { ...btnPrimary, padding: '7px 12px' } : small}>
                  {a.label}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
}
